import { EMPTY_OBJECT } from 'constants/defaults';
import { last } from 'lodash-es';
import type { IState } from 'redux/state';
import { createSelector } from 'reselect';
import type { ViewContext } from 'types/analytics';
import { decodeUrlParams } from 'utils/url-params';

const readRouting = (state: IState) => state.router;

export const getRouting = readRouting;

export const getRoutePathName = createSelector([getRouting], (routing): string => {
  if (routing.location) {
    return routing.location.pathname;
  }
  return '/';
});

export const getRouteSearchAsObject = createSelector(
  [getRouting],
  (routing): Record<string, string> => {
    if (routing.location) {
      return decodeUrlParams(routing.location.search);
    }
    return EMPTY_OBJECT;
  }
);

export const getIsRouteOnboarding = createSelector([getRoutePathName], (pathname): boolean => {
  return pathname.includes('welcome');
});

export const getIsRouteCalendar = createSelector([getRoutePathName], (pathname): boolean => {
  return pathname.includes('calendar');
});

export const getIsRouteReports = createSelector([getRoutePathName], (pathname): boolean => {
  return pathname.includes('superreports');
});

export const getIsRouteReport = createSelector([getRoutePathName], (pathname): boolean => {
  return pathname.includes('report');
});

export const getIsRouteTimesheets = createSelector([getRoutePathName], pathname => {
  return pathname.includes('hours');
});

export const getIsProjectActivitiesRoute = createSelector(
  [getRoutePathName],
  (pathname): boolean => {
    const isProjectsDashboard = pathname.includes('projects/dashboard');
    const isProjectDetailsActivities =
      pathname.includes('projects') && pathname.includes('activities');
    return isProjectsDashboard || isProjectDetailsActivities;
  }
);

export const getRouteSegments = createSelector([getRoutePathName], pathname =>
  pathname.split('/').filter(part => !!part && !/^[\d]+$/.test(part))
);

export const getViewFromRoute = createSelector(
  [getRouteSegments],
  (routeSegments: Array<string>): ViewContext => {
    const [mainRoute] = routeSegments;
    const lastRouteSegment = last(routeSegments);
    if (mainRoute === 'calendar') {
      return 'Calendar';
    }
    if (mainRoute === 'tasks') {
      return 'Tasks';
    }
    if (lastRouteSegment === 'hours') {
      return 'Timesheet';
    }
    if (mainRoute === 'superreports' || lastRouteSegment === 'report') {
      return 'Report';
    }
    if (mainRoute === 'users') {
      return 'Users';
    }
    if (mainRoute === 'projects') {
      return 'Projects';
    }
    return 'Global';
  }
);
